import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { User, Mail, Calendar, FileText, Shield, Lock, ArrowLeft, Check, Bell } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const Profile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [fullName, setFullName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [togglingNotifs, setTogglingNotifs] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchProfile();
  }, [user, navigate]);

  const fetchProfile = async () => {
    try {
      const data = await api.getProfile();
      setProfile(data);
      setFullName(data.full_name);
      // Fetch notification preferences
      try {
        const notifData = await api.getNotificationPreferences();
        setEmailNotifications(notifData.email_notifications);
      } catch (e) { /* default to true */ }
    } catch (error) {
      toast.error('Error al cargar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNotifications = async () => {
    setTogglingNotifs(true);
    try {
      const data = await api.toggleNotifications();
      setEmailNotifications(data.email_notifications);
      toast.success(data.email_notifications ? 'Notificaciones activadas' : 'Notificaciones desactivadas');
    } catch (error) {
      toast.error('Error al actualizar preferencias');
    } finally {
      setTogglingNotifs(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!fullName.trim()) {
      toast.error('El nombre no puede estar vacío');
      return;
    }
    
    setSaving(true);
    try {
      await api.updateProfile({ full_name: fullName });
      toast.success('Perfil actualizado');
      setEditing(false);
      fetchProfile();
    } catch (error) {
      toast.error('Error al actualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error('Completa todos los campos');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('La nueva contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    setSaving(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      toast.success('Contraseña actualizada');
      setChangingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F0' }}>
      <nav className="bg-white border-b border-border shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-14">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/assets/logo.png" alt="logo" className="h-8 w-auto" />
              <span className="text-lg font-semibold" style={{ color: '#1A4D2E' }}>ContractCheck360</span>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 md:px-8 max-w-3xl py-8">
        <h1 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#1A4D2E' }}>
          Mi Perfil
        </h1>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: '#1A4D2E' }}></div>
          </div>
        ) : profile ? (
          <div className="space-y-6">
            {/* Profile Info Card */}
            <div className="bg-white border border-border shadow-sm rounded-lg p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white"
                     style={{ backgroundColor: '#1A4D2E' }}>
                  {profile.full_name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-xl font-semibold">{profile.full_name}</h2>
                  <p className="text-sm text-gray-500">{profile.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm font-medium">{profile.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Shield className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Rol</p>
                    <p className="text-sm font-medium capitalize">{profile.role}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Contratos</p>
                    <p className="text-sm font-medium">{profile.total_contracts} subidos, {profile.total_analyses} analizados</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Miembro desde</p>
                    <p className="text-sm font-medium">{new Date(profile.created_at).toLocaleDateString('es-MX')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Edit Name Card */}
            <div className="bg-white border border-border shadow-sm rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <User className="w-5 h-5" style={{ color: '#1A4D2E' }} />
                  Nombre
                </h3>
                {!editing && (
                  <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                    Editar
                  </Button>
                )}
              </div>

              {editing ? (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                    style={{ focusRing: '#1A4D2E' }}
                    placeholder="Tu nombre completo"
                    data-testid="edit-name-input"
                  />
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleUpdateProfile}
                      disabled={saving}
                      style={{ backgroundColor: '#1A4D2E' }}
                      data-testid="save-name-btn"
                    >
                      {saving ? 'Guardando...' : 'Guardar'}
                    </Button>
                    <Button variant="outline" onClick={() => { setEditing(false); setFullName(profile.full_name); }}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-700">{profile.full_name}</p>
              )}
            </div>

            {/* Change Password Card */}
            <div className="bg-white border border-border shadow-sm rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Lock className="w-5 h-5" style={{ color: '#1A4D2E' }} />
                  Contraseña
                </h3>
                {!changingPassword && (
                  <Button variant="outline" size="sm" onClick={() => setChangingPassword(true)}>
                    Cambiar
                  </Button>
                )}
              </div>

              {changingPassword ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Contraseña actual</label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                      placeholder="••••••••"
                      data-testid="current-password-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Nueva contraseña</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                      placeholder="Mínimo 8 caracteres"
                      data-testid="new-password-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Confirmar nueva contraseña</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                      placeholder="Repite la nueva contraseña"
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleChangePassword}
                      disabled={saving}
                      style={{ backgroundColor: '#1A4D2E' }}
                      data-testid="change-password-btn"
                    >
                      {saving ? 'Guardando...' : 'Cambiar Contraseña'}
                    </Button>
                    <Button variant="outline" onClick={() => { 
                      setChangingPassword(false); 
                      setCurrentPassword(''); 
                      setNewPassword(''); 
                      setConfirmPassword(''); 
                    }}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">••••••••••••</p>
              )}
            </div>

            {/* Email Notifications Card */}
            <div className="bg-white border border-border shadow-sm rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full" style={{ backgroundColor: emailNotifications ? '#ECFDF5' : '#F3F4F6' }}>
                    <Bell className="w-5 h-5" style={{ color: emailNotifications ? '#1A4D2E' : '#9CA3AF' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold">Notificaciones por Email</h3>
                    <p className="text-sm text-gray-500">
                      Recibe alertas cuando tus contratos esten por vencer
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleToggleNotifications}
                  disabled={togglingNotifs}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    emailNotifications ? 'bg-green-700' : 'bg-gray-300'
                  }`}
                  style={emailNotifications ? { backgroundColor: '#1A4D2E' } : {}}
                  data-testid="toggle-email-notifications"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      emailNotifications ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-gray-500">No se pudo cargar el perfil</p>
        )}
      </div>
    </div>
  );
};

export default Profile;
